const LOG_UNHANDLED_UPDATES = ((name) => {
  try {
    const value = localStorage.getItem(name);
    if (value !== null) {
      return Boolean(JSON.parse(value));
    } else {
      // NOTE: Eventually (once things quiet down) we'll flip this to __DEV__
      return false;
    }
  } catch (ex) {}

  return false;
})('bridgeLog');
const THROW_ON_ERRORS = LOG_UNHANDLED_UPDATES;

// tslint:disable-next-line:no-any
const logError = (...args: any[]) => {
  if (LOG_UNHANDLED_UPDATES) {
    console.error(...args);
  }
};

export interface Update {
  model: 'board' | 'card' | 'member';
  id: string;
  // tslint:disable-next-line:no-any
  changes: any;
}

type Callback = (updates: Update[]) => void;
type GetAll = () => Update[];
type WebSocketSubscriber = (type: string, id: string, tags?: string[]) => void;
interface RestData {
  [key: string]: undefined | string | string[] | number | boolean;
}

export interface SharedData {
  url: string;
  args: RestData;
}

export interface RequestData extends SharedData {
  method: ApiMethod.put | ApiMethod.post | ApiMethod.delete;
}

export interface ResponseData extends SharedData {
  method: ApiMethod;
  // tslint:disable-next-line:no-any
  response: any;
}

export enum ApiMethod {
  get = 'GET',
  put = 'PUT',
  post = 'POST',
  delete = 'DELETE',
}

export enum ApiArea {
  board,
  card,
  member,
}

interface ApiPathData {
  area: ApiArea;
  pathParts: string[];
}

type Updater = (
  area: ApiArea,
  pathParts: string[],
  data: ResponseData | RequestData,
) => Update[] | null;

// NOTE: Currently we don't have any updaters
const updaters: Updater[] = [];

function getApiPathData(urlPath: string): ApiPathData | null {
  if (/^https?:/i.test(urlPath)) {
    if (__DEV__) {
      if (THROW_ON_ERRORS) {
        throw new Error('Did not expect to receive a URL with a protocol');
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
  // (?:\/?1)?   Ignore a leading /1 or 1
  // \/?         Ignore a leading /
  // ([a-z]+?)s  The area of the API we're dealing with (ignore trailing s)
  // \/?         Ignore the / that might be at the start of the path
  // (.*)$       Everything else is the path
  // \/?         Ignore a trailing /
  const parts = /^(?:\/?1)?\/?([a-z]+?)s\/?(.*)\/?$/i.exec(urlPath);

  if (parts) {
    const [, area, path] = parts;
    const lcArea = area.toLowerCase();

    if (lcArea === 'board' || lcArea === 'card' || lcArea === 'member') {
      return {
        area: ApiArea[lcArea],
        pathParts: path.length > 0 ? path.split('/') : [],
      };
    } else if (LOG_UNHANDLED_UPDATES) {
      logError(`Unknown API area ${lcArea}`);
    }
  } else {
    logError(`Unable to parse URL ${urlPath} for possible updates`);
  }

  return null;
}

function updatesFrom(data: RequestData | ResponseData): Update[] {
  const urlData = getApiPathData(data.url);

  if (urlData) {
    const updates = updaters
      .map((updater) => updater(urlData.area, urlData.pathParts, data))
      .filter((entries): entries is Update[] => entries !== null);

    switch (updates.length) {
      case 0:
        if (LOG_UNHANDLED_UPDATES) {
          logError(
            `Did not know how to extract updates from ${data.method} ${
              data.url
            }`,
          );
        }

        return [];

      case 1:
        return updates[0];

      default:
        if (__DEV__) {
          logError(
            `Only expected one set of updates for ${data.method} ${data.url}`,
          );
        }

        return [];
    }
  } else {
    if (__DEV__ && THROW_ON_ERRORS) {
      throw new Error(`Unable to parse URL ${data.url}`);
    } else {
      return [];
    }
  }
}

const flatten = <T>(list: T[][]): T[] => ([] as T[]).concat(...list);
class BridgeConnection {
  name: string;
  others: BridgeConnection[] = [];
  subscribers: Callback[] = [];
  updatesFromStores: GetAll[] = [];
  webSocketSubscriber: WebSocketSubscriber | undefined = undefined;
  pendingWebSocketSubscriptions: ((
    weSubscriber: WebSocketSubscriber,
  ) => void)[] = [];

  constructor(name: string) {
    this.name = name;
  }

  setWebSocketSubscriber(wsSubscriber: WebSocketSubscriber) {
    this.webSocketSubscriber = wsSubscriber;

    this.pendingWebSocketSubscriptions.forEach((callback) =>
      callback(wsSubscriber),
    );
    this.pendingWebSocketSubscriptions = [];

    this.others.forEach((other) => {
      if (!other.webSocketSubscriber) {
        other.setWebSocketSubscriber(wsSubscriber);
      }
    });
  }

  addOther(other: BridgeConnection) {
    this.others.push(other);
  }

  inform(data: RequestData | ResponseData) {
    this.update(updatesFrom(data));
  }

  update(updates: Update[]) {
    if (updates.length > 0) {
      this.others.forEach((connection) => connection.notifySubscribed(updates));
    }
  }

  subscribe(callback: Callback) {
    this.subscribers.push(callback);

    const updates = flatten(
      this.others.map((connection) =>
        flatten(connection.updatesFromStores.map((getUpdates) => getUpdates())),
      ),
    );

    if (updates.length > 0) {
      callback(updates);
    }
  }

  notifySubscribed(updates: Update[]) {
    this.subscribers.forEach((callback) => {
      callback(updates);
    });
  }

  addModelStore(updatesFromStore: GetAll) {
    this.updatesFromStores.push(updatesFromStore);

    if (this.others.length > 0) {
      const updates = updatesFromStore();
      if (updates.length > 0) {
        this.others.forEach((connection) =>
          connection.notifySubscribed(updates),
        );
      }
    }
  }

  wsSubscribe(model: string, id: string, tags?: string[]) {
    if (this.webSocketSubscriber !== undefined) {
      this.webSocketSubscriber(model, id, tags);
    } else {
      this.pendingWebSocketSubscriptions.push((wsSubscribe) => {
        wsSubscribe(model, id, tags);
      });
    }
  }
}

class Bridge {
  connections: BridgeConnection[] = [];

  connect(name: string): BridgeConnection {
    const connection = new BridgeConnection(name);

    this.connections.forEach((other) => {
      connection.addOther(other);
      other.addOther(connection);
    });

    this.connections.push(connection);

    return connection;
  }
}

export default new Bridge();



// WEBPACK FOOTER //
// ./app/common/network/bridge/index.ts